/**
 * ExecutionLogViewer
 * Shared component for displaying execution logs with grouping, filtering, and styling
 * Used in both ExecutionPanel (canvas) and Executions page
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useUserTimezone } from '@/hooks/useDateFormat';
import { Button } from '@/components/ui/button';
import { displayLogMessage } from '@/utils/logDisplay';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Filter,
  Copy,
  Check,
  RefreshCw
} from 'lucide-react';

export interface ExecutionLog {
  timestamp: Date | string;
  level: 'info' | 'warn' | 'warning' | 'error' | 'debug';
  message: string;
  data?: any;
}

export interface ExecutionLogViewerProps {
  logs: ExecutionLog[];
  isLive?: boolean;
  autoScroll?: boolean;
  showFilters?: boolean;
  showExport?: boolean;
  maxHeight?: string;
  /**
   * Fill the parent flex container's height instead of using `maxHeight`. The
   * root becomes `h-full min-h-0` and the scroll region flexes to fill — used
   * when the viewer is the sole scroll region of a tab panel, so there's no
   * nested "double scroll". Overrides `maxHeight`.
   */
  fillHeight?: boolean;
  className?: string;
}

type LogPhase = 'setup' | 'execution' | 'cleanup' | 'unknown';
type LogLevel = 'all' | 'info' | 'warning' | 'error' | 'debug';

interface GroupedLogs {
  phase: LogPhase;
  label: string;
  logs: ExecutionLog[];
  startTime?: Date;
  endTime?: Date;
  duration?: number;
}

// Detect log phase based on message content
function detectPhase(message: string): LogPhase {
  const upperMsg = message.toUpperCase();

  // Setup phase markers
  if (
    upperMsg.includes('EXECUTION STARTED') ||
    upperMsg.includes('ESIM PROFILE ALLOCATION') ||
    upperMsg.includes('MODEM ALLOCATION') ||
    upperMsg.includes('LOCATIONS RESOLVED') ||
    upperMsg.includes('CLUSTER EXECUTION STARTED')
  ) {
    return 'setup';
  }

  // Cleanup phase markers
  if (
    upperMsg.includes('RESOURCE CLEANUP') ||
    upperMsg.includes('RELEASED') ||
    upperMsg.includes('EXECUTION COMPLETE') ||
    upperMsg.includes('EXECUTION FAILED')
  ) {
    return 'cleanup';
  }

  // Execution phase - everything else that looks like node execution
  if (
    upperMsg.includes('NODE:') ||
    upperMsg.includes('AT COMMAND:') ||
    upperMsg.includes('FLOW EXECUTION') ||
    upperMsg.includes('RESPONSE:') ||
    upperMsg.includes('PARALLEL EXECUTION')
  ) {
    return 'execution';
  }

  return 'unknown';
}

// Get phase display info
function getPhaseInfo(phase: LogPhase): { label: string } {
  switch (phase) {
    case 'setup':
      return { label: 'Setup' };
    case 'execution':
      return { label: 'Execution' };
    case 'cleanup':
      return { label: 'Cleanup' };
    default:
      return { label: 'Other' };
  }
}

// Helper to convert timestamp to Date
function toDate(timestamp: Date | string | undefined): Date | undefined {
  if (!timestamp) return undefined;
  return timestamp instanceof Date ? timestamp : new Date(timestamp);
}

// Group logs by phase
function groupLogsByPhase(logs: ExecutionLog[]): GroupedLogs[] {
  const groups: GroupedLogs[] = [];
  let currentPhase: LogPhase | null = null;
  let currentGroup: ExecutionLog[] = [];

  for (const log of logs) {
    const phase = detectPhase(log.message);

    // If phase changed and we have logs, save the current group
    if (phase !== currentPhase && phase !== 'unknown' && currentPhase !== null) {
      if (currentGroup.length > 0) {
        const phaseInfo = getPhaseInfo(currentPhase);
        groups.push({
          phase: currentPhase,
          ...phaseInfo,
          logs: currentGroup,
          startTime: toDate(currentGroup[0]?.timestamp),
          endTime: toDate(currentGroup[currentGroup.length - 1]?.timestamp)
        });
      }
      currentGroup = [];
      currentPhase = phase;
    } else if (currentPhase === null && phase !== 'unknown') {
      currentPhase = phase;
    }

    currentGroup.push(log);
  }

  // Don't forget the last group
  if (currentGroup.length > 0 && currentPhase) {
    const phaseInfo = getPhaseInfo(currentPhase);
    groups.push({
      phase: currentPhase,
      ...phaseInfo,
      logs: currentGroup,
      startTime: toDate(currentGroup[0]?.timestamp),
      endTime: toDate(currentGroup[currentGroup.length - 1]?.timestamp)
    });
  }

  // Calculate durations
  for (const group of groups) {
    if (group.startTime && group.endTime) {
      group.duration = (group.endTime.getTime() - group.startTime.getTime()) / 1000;
    }
  }

  return groups;
}

// Format timestamp for display
function formatTimestamp(timestamp: Date | string, timeZone: string): string {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  // Format with milliseconds manually since fractionalSecondDigits isn't in TS types
  const timeStr = date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone
  });
  const ms = date.getMilliseconds().toString().padStart(3, '0');
  return `${timeStr}.${ms}`;
}

// Get log level color class
function getLevelColorClass(level: string): string {
  switch (level) {
    case 'error':
      return 'text-accent-red';
    case 'warn':
    case 'warning':
      return 'text-accent-orange';
    case 'debug':
      return 'text-muted-foreground/50';
    case 'info':
    default:
      return 'text-muted-foreground';
  }
}

// Get message styling based on content
function getMessageStyling(message: string): string {
  // AT Command styling
  if (message.includes('AT COMMAND:') || message.includes('📤')) {
    return 'text-accent-blue font-medium';
  }
  // Response styling
  if (message.includes('RESPONSE:') || message.includes('📥')) {
    return 'text-accent-green';
  }
  // Error styling
  if (message.includes('❌') || message.includes('FAILED')) {
    return 'text-accent-red';
  }
  // Success styling
  if (message.includes('✅') || message.includes('✓')) {
    return 'text-accent-green';
  }
  // Header styling (all caps sections)
  if (/^[🚀📍📱▶️💳📡⏳📊🔓]/.test(message.trim())) {
    return 'font-medium';
  }
  return '';
}

export function ExecutionLogViewer({
  logs,
  isLive = false,
  autoScroll = true,
  showFilters = true,
  showExport = true,
  maxHeight = '400px',
  fillHeight = false,
  className
}: ExecutionLogViewerProps) {
  const [levelFilter, setLevelFilter] = useState<LogLevel>('all');
  const [groupByPhase, setGroupByPhase] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set(['setup', 'execution', 'cleanup']));
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const timeZone = useUserTimezone();

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && isLive && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs.length, autoScroll, isLive]);

  // Filter logs by level
  const filteredLogs = useMemo(() => {
    if (levelFilter === 'all') return logs;
    return logs.filter(log => {
      if (levelFilter === 'warning') {
        return log.level === 'warn' || log.level === 'warning';
      }
      return log.level === levelFilter;
    });
  }, [logs, levelFilter]);

  // Group logs if enabled
  const groupedLogs = useMemo(() => {
    if (!groupByPhase) return null;
    return groupLogsByPhase(filteredLogs);
  }, [filteredLogs, groupByPhase]);

  // Toggle phase expansion
  const togglePhase = (phase: string) => {
    const newExpanded = new Set(expandedPhases);
    if (newExpanded.has(phase)) {
      newExpanded.delete(phase);
    } else {
      newExpanded.add(phase);
    }
    setExpandedPhases(newExpanded);
  };

  // Copy logs to clipboard
  const copyLogs = async () => {
    const text = logs.map(log => {
      const ts = formatTimestamp(log.timestamp, timeZone);
      return `[${ts}] [${log.level.toUpperCase()}] ${displayLogMessage(log.message)}`;
    }).join('\n');

    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Export logs as JSON
  const exportLogs = () => {
    const data = JSON.stringify(logs, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execution-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render a single log entry
  const renderLogEntry = (log: ExecutionLog, index: number) => {
    const ts = formatTimestamp(log.timestamp, timeZone);
    const levelColor = getLevelColorClass(log.level);
    // Colour from the stored message (its emoji markers still say what the
    // line is); the text shown is the same message without them.
    const messageStyle = getMessageStyling(log.message);

    return (
      <div
        key={index}
        className={cn(
          'py-0.5 font-mono text-sm leading-relaxed',
          levelColor
        )}
      >
        <span className="text-muted-foreground/50">[{ts}]</span>{' '}
        <span className={cn('uppercase text-xs', levelColor)}>
          [{log.level}]
        </span>{' '}
        <span className={messageStyle}>{displayLogMessage(log.message)}</span>
      </div>
    );
  };

  // Render grouped view
  const renderGroupedView = () => {
    if (!groupedLogs || groupedLogs.length === 0) {
      return (
        <div className="text-muted-foreground text-center py-8">
          No logs available
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {groupedLogs.map((group, groupIndex) => {
          const isExpanded = expandedPhases.has(group.phase);
          const duration = group.duration ? `${group.duration.toFixed(1)}s` : '';

          return (
            <div key={`${group.phase}-${groupIndex}`} className="border border-border rounded">
              {/* Phase Header */}
              <button
                onClick={() => togglePhase(group.phase)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className="font-medium text-foreground">{group.label}</span>
                  <span className="text-muted-foreground text-sm">
                    ({group.logs.length} entries)
                  </span>
                </div>
                {duration && (
                  <span className="text-muted-foreground text-sm">{duration}</span>
                )}
              </button>

              {/* Phase Logs */}
              {isExpanded && (
                <div className="border-t border-border px-3 py-2 bg-background">
                  {group.logs.map((log, logIndex) => renderLogEntry(log, logIndex))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render flat view
  const renderFlatView = () => {
    if (filteredLogs.length === 0) {
      return (
        <div className="text-muted-foreground text-center py-8">
          No logs available for this execution
        </div>
      );
    }

    return filteredLogs.map((log, index) => renderLogEntry(log, index));
  };

  return (
    <div className={cn('flex flex-col', fillHeight && 'min-h-0', className)}>
      {/* Toolbar */}
      {(showFilters || showExport) && (
        <div className="flex flex-shrink-0 items-center justify-between gap-2 mb-3">
          {/* Filters */}
          {showFilters && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value as LogLevel)}
                className="text-sm bg-background border border-border rounded px-2 py-1"
              >
                <option value="all">All Levels</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="debug">Debug</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setGroupByPhase(!groupByPhase)}
                className={cn(groupByPhase && 'bg-muted')}
              >
                Group by Phase
              </Button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-1">
            {isLive && (
              <div className="flex items-center gap-1 text-accent-green text-sm mr-2">
                <RefreshCw className="w-3 h-3 animate-spin" />
                Live
              </div>
            )}
            {showExport && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyLogs}
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-accent-green" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={exportLogs}
                  title="Download JSON"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Log Content */}
      <div
        ref={scrollRef}
        className={cn(
          'bg-background rounded p-4 overflow-y-auto',
          fillHeight && 'flex-1 min-h-0',
        )}
        style={fillHeight ? undefined : { maxHeight }}
      >
        {groupByPhase ? renderGroupedView() : renderFlatView()}
      </div>

      {/* Summary */}
      <div className="flex flex-shrink-0 items-center justify-between mt-2 text-xs text-muted-foreground">
        <span>
          {filteredLogs.length} log entries
          {levelFilter !== 'all' && ` (filtered from ${logs.length})`}
        </span>
        {logs.length > 0 && logs[logs.length - 1]?.timestamp && (
          <span>
            Last update: {formatTimestamp(logs[logs.length - 1].timestamp, timeZone)}
          </span>
        )}
      </div>
    </div>
  );
}
